import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useDeleteCapabilityOverride } from "@/hooks/useCapabilities";
import type { CapabilityOverride } from "@/services/capabilitiesApi";

interface DeleteCapabilityOverrideDialogProps {
  override: CapabilityOverride | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DeleteCapabilityOverrideDialog({
  override,
  open,
  onOpenChange,
}: DeleteCapabilityOverrideDialogProps) {
  const deleteMutation = useDeleteCapabilityOverride();

  if (!override) return null;

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(override.id);
      toast.success("Override deleted");
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to delete override",
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete Override</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Delete the{" "}
          <span className="font-medium text-foreground">{override.state}</span>{" "}
          override for{" "}
          <span className="font-medium text-foreground">
            {override.capability}
          </span>{" "}
          at {override.scope_type}
          {override.scope_id ? ` (${override.scope_id})` : ""}? The effective
          decision reverts to inherit. This cannot be undone.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending && (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            )}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
